import Fuse from 'fuse.js';
import { useEffect, useMemo, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_DELAY = 1200; // in milliseconds

interface Option {
	value: string;
	label: string;
	group?: string;
	meta?: string;
}

interface AdvancedSelectProps {
	disabled?: boolean;
	isSearchable?: boolean;
	label?: string;
	name: string;
	onChange: (value: string) => void;
	onSearch: (value: string) => void;
	options: Option[];
	placeholder?: string;
	required?: boolean;
	value: string;
}

const createFuseInstance = (options: Option[]) =>
	new Fuse(options, {
		keys: ['label', 'group'],
		threshold: 0.2,
	});

const organizeOptions = (options: Option[]) => {
	const groupedOptions = options.reduce(
		(acc, option: Option) => {
			if (option.group) {
				if (!acc[option.group]) {
					acc[option.group] = [];
				}
				acc[option.group].push(option);
			}
			return acc;
		},
		{} as Record<string, Option[]>,
	);

	const ungroupedOptions = options.filter((option) => !option.group);

	return { groupedOptions, ungroupedOptions };
};

export default function AdvancedSelect({
	disabled = false,
	isSearchable = false,
	label = '',
	name,
	onChange,
	onSearch,
	options,
	placeholder = '',
	required = false,
	value,
}: AdvancedSelectProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const selectRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Find the selected option
	const selectedOption = options.find((option) => option.value === value);

	// Memoize Fuse instance
	const fuse = useMemo(() => createFuseInstance(options), [options]);

	// Filter options based on search query
	const filteredOptions = isSearchable && searchQuery ? fuse.search(searchQuery).map((result) => result.item) : options;

	const { groupedOptions, ungroupedOptions } = useMemo(() => organizeOptions(filteredOptions), [filteredOptions]);

	// Generate class names for the advanced select container
	const selectClasses = ['advanced-select'];
	if (isOpen) selectClasses.push('advanced-select--open');
	if (disabled) selectClasses.push('advanced-select--disabled');

	// Handle option selection
	const onOptionClick = (optionValue: string) => {
		if (optionValue !== value) {
			onChange(optionValue);
		}
		setIsOpen(false);
		setSearchQuery('');
		triggerRef.current?.focus();
	};

	// Open or close the dropdown on click
	const handleTriggerClick = () => {
		if (disabled) return;
		setIsOpen(!isOpen);
	};

	const renderOption = (option: Option, localIndex: number) => {
		const optionClasses = ['advanced-select__option', option.value === value && 'advanced-select__option--selected'].filter(Boolean).join(' ');

		return (
			<li
				key={`${option.value}-${localIndex}`}
				className={optionClasses}
				role="option"
				aria-selected={option.value === value ? 'true' : 'false'}
				onClick={() => onOptionClick(option.value)}
			>
				<span className="label">{option.label}</span>
				{option.meta && <span className="meta">{option.meta}</span>}
			</li>
		);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setSearchQuery('');
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isOpen && isSearchable) {
			searchInputRef.current?.focus();
		}
	}, [isOpen, isSearchable]);

	// Handle onSearch callback
	useEffect(() => {
		if (isSearchable && searchQuery !== '') {
			// Set new timeout if search query is not empty
			timeoutRef.current = setTimeout(() => {
				onSearch(searchQuery);
			}, SEARCH_DEBOUNCE_DELAY);
		}

		// Cleanup on unmount or when dependencies change
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [isSearchable, searchQuery, onSearch]);

	return (
		<div className="form-field" data-field-type="advanced-select" data-field-name={name}>
			{label && (
				<label id={`${name}-label`} htmlFor={`${name}-trigger`} className="form-field__label">
					{label}
				</label>
			)}

			<div ref={selectRef} className={selectClasses.join(' ')}>
				<button
					ref={triggerRef}
					id={`${name}-trigger`}
					type="button"
					className="advanced-select__trigger"
					onClick={handleTriggerClick}
					disabled={disabled}
					aria-haspopup="listbox"
					aria-expanded={isOpen ? 'true' : 'false'}
					aria-labelledby={label ? `${name}-label` : undefined}
				>
					<span className="advanced-select__value">{selectedOption ? selectedOption.label : placeholder}</span>
					<span className="advanced-select__arrow" aria-hidden="true">
						keyboard_arrow_down
					</span>
				</button>

				{isOpen && (
					<div className="advanced-select__dropdown">
						{isSearchable && (
							<div className="advanced-select__search">
								<input
									ref={searchInputRef}
									type="text"
									className="advanced-select__search-input"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Escape') {
											e.preventDefault();
											setIsOpen(false);
											setSearchQuery('');
											triggerRef.current?.focus();
										}
									}}
								/>
								<span className="advanced-select__search-icon" aria-hidden="true">
									search
								</span>
							</div>
						)}

						<ul className="advanced-select__list" role="listbox" aria-labelledby={`${name}-trigger`}>
							{/* Render ungrouped options */}
							{ungroupedOptions.map((option, index) => renderOption(option, index))}

							{/* Render grouped options */}
							{Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
								<li className="advanced-select__group" key={`group-${groupName}`}>
									<span className="advanced-select__group-label">{groupName}</span>
									<ul className="advanced-select__group-list" role="listbox" aria-labelledby={`${name}-trigger`}>
										{groupOptions.map((option, index) => renderOption(option, index))}
									</ul>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>

			{/* Hidden input for form submission */}
			<input type="hidden" name={name} value={value} required={required} />
		</div>
	);
}
